import type { VariantProps } from '@joyful/utils'
import { tv } from '@joyful/utils'
import type { ComponentProps, ReactNode } from 'react'
import { createContext, use } from 'react'

import { Copyable } from '../Copyable'
import { Stack } from '../Stack'

const dataFieldListVariants = tv({
  base: 'grid',

  compoundVariants: [
    {
      className: 'gap-x-3 gap-y-2',
      direction: ['horizontal', 'inline'],
      size: 'lg',
    },

    {
      className: 'gap-x-3 gap-y-2',
      direction: ['horizontal', 'inline'],
      size: 'md',
    },

    {
      className: 'gap-x-3 gap-y-1.5',
      direction: ['horizontal', 'inline'],
      size: 'sm',
    },

    {
      className: 'gap-x-3 gap-y-2',
      direction: ['horizontal', 'inline'],
      size: 'xl',
    },

    {
      className: 'gap-x-2 gap-y-0.5',
      direction: 'vertical',
      size: 'md',
    },

    {
      className: 'grid-cols-[max-content_minmax(0,1fr)]',
      direction: 'horizontal',
      labelWidth: 'fit',
    },

    {
      className:
        'auto-rows-max grid-cols-1 gap-y-0.5 @md:grid-cols-[max-content_minmax(0,1fr)] @md:gap-x-3 @md:gap-y-1.5',
      direction: 'containerFit',
      labelWidth: 'fit',
      size: 'sm',
    },

    {
      className:
        'auto-rows-max grid-cols-1 gap-y-0.5 @md:grid-cols-[max-content_minmax(0,1fr)] @md:gap-x-3 @md:gap-y-2',
      direction: 'containerFit',
      labelWidth: 'fit',
      size: 'md',
    },

    {
      className: 'grid-cols-[9rem_minmax(0,1fr)]',
      direction: 'horizontal',
      labelWidth: 'sm',
    },

    {
      className: 'grid-cols-[10rem_minmax(0,1fr)]',
      direction: 'horizontal',
      labelWidth: 'md',
    },
  ],

  defaultVariants: {
    direction: 'horizontal',
    size: 'md',
  },

  variants: {
    align: {
      center: 'items-center',
    },

    direction: {
      containerFit: null,
      horizontal: 'grid-cols-[minmax(min-content,1fr)_minmax(0,1fr)]',
      inline: 'grid-flow-col auto-cols-max',
      vertical: 'auto-rows-max grid-cols-1',
    },

    labelWidth: {
      fit: null,
      md: null,
      sm: null,
    },

    overflow: {
      hidden: 'overflow-hidden',
    },

    padding: {
      md: 'p-4',
    },

    paddingY: {
      sm: 'py-2',
    },

    size: {
      lg: 'text-base',
      md: 'text-sm',
      sm: 'text-xs',
      xl: 'text-lg',
    },
  },
})

type DataFieldContextValue = Omit<
  VariantProps<typeof dataFieldListVariants>,
  'align' | 'labelWidth' | 'overflow' | 'padding' | 'paddingY'
>

const DataFieldContext = createContext<DataFieldContextValue>(
  dataFieldListVariants.defaultVariants,
)

export interface DataFieldItemProps
  extends
    Omit<ComponentProps<'dl'>, 'className'>,
    VariantProps<typeof dataFieldListVariants> {}

export function DataFieldItem({
  align,
  children,
  direction,
  labelWidth,
  overflow,
  padding,
  paddingY,
  size,
  ...props
}: DataFieldItemProps) {
  const context = use(DataFieldContext)

  return (
    <DataFieldContext
      value={{
        direction: direction ?? context.direction,
        size: size ?? context.size,
      }}
    >
      <dl
        {...props}
        className={dataFieldListVariants({
          align,
          direction: direction ?? context.direction,
          labelWidth,
          overflow,
          padding,
          paddingY,
          size: size ?? context.size,
        })}
      >
        {children}
      </dl>
    </DataFieldContext>
  )
}

export type DataFieldListProps = DataFieldItemProps

export function DataFieldList({ children, ...props }: DataFieldListProps) {
  return <DataFieldItem {...props}>{children}</DataFieldItem>
}

const dataFieldLabelVariants = tv({
  base: 'text-tremor-content col-start-1 min-w-min pt-0 pb-0',

  variants: {
    hasAdornment: {
      true: 'inline-flex items-center gap-x-0.5',
    },
    tone: {
      emphasis: 'text-tremor-content-emphasis',
    },
    weight: {
      medium: 'font-medium',
      semibold: 'font-semibold',
    },
  },
})

export interface DataFieldLabelProps
  extends
    Omit<ComponentProps<'dt'>, 'className'>,
    Omit<VariantProps<typeof dataFieldLabelVariants>, 'hasAdornment'> {
  endAdornment?: ReactNode
  startAdornment?: ReactNode
}

export function DataFieldLabel({
  children,
  endAdornment,
  startAdornment,
  tone,
  weight,
  ...props
}: DataFieldLabelProps) {
  const hasAdornment =
    startAdornment !== undefined || endAdornment !== undefined

  return (
    <dt
      {...props}
      className={dataFieldLabelVariants({ hasAdornment, tone, weight })}
    >
      {startAdornment}
      {children}
      {endAdornment}
    </dt>
  )
}

const dataFieldValueVariants = tv({
  base: 'min-w-0 wrap-break-word',

  defaultVariants: {
    tone: 'strong',
  },

  variants: {
    align: {
      end: 'justify-self-end text-end',
    },
    overflowBehavior: {
      truncate: 'truncate',
    },
    tone: {
      muted: 'text-tremor-content',
      quote: 'text-tremor-content italic',
      strong: 'text-tremor-content-strong',
    },
    weight: {
      medium: 'font-medium',
      mono: 'font-mono',
      semibold: 'font-semibold',
    },
    wrap: {
      anywhere: 'break-all',
    },
  },
})

export interface DataFieldValueProps
  extends
    Omit<ComponentProps<'dd'>, 'className'>,
    VariantProps<typeof dataFieldValueVariants> {}

export function DataFieldValue({
  align,
  children,
  overflowBehavior,
  tone,
  weight,
  wrap,
  ...props
}: DataFieldValueProps) {
  return (
    <dd
      {...props}
      className={dataFieldValueVariants({
        align,
        overflowBehavior,
        tone,
        weight,
        wrap,
      })}
    >
      {/* oxlint-disable-next-line anti-slop/no-runtime-typeof */}
      {typeof children === 'string' ? (
        <Copyable>{children}</Copyable>
      ) : (
        children
      )}
    </dd>
  )
}

export interface DataFieldProps extends VariantProps<
  typeof dataFieldValueVariants
> {
  children: ReactNode
  term: ReactNode
  termEndAdornment?: ReactNode
}

export function DataField({
  children,
  term,
  termEndAdornment,
  ...valueProps
}: DataFieldProps) {
  return (
    <>
      {/* oxlint-disable-next-line anti-slop/no-runtime-typeof */}
      {typeof term === 'string' ? (
        <DataFieldLabel endAdornment={termEndAdornment}>{term}</DataFieldLabel>
      ) : (
        term
      )}
      <DataFieldValue {...valueProps}>{children}</DataFieldValue>
    </>
  )
}

export function DataFieldColumn({
  children,
  direction,
  size,
}: DataFieldContextValue & {
  children: ReactNode
}) {
  const context = use(DataFieldContext)
  return (
    <DataFieldContext
      value={{
        direction: direction ?? context.direction,
        size: size ?? context.size,
      }}
    >
      <Stack gap="sm">{children}</Stack>
    </DataFieldContext>
  )
}

export interface DataFieldColumnListProps
  extends Omit<ComponentProps<'div'>, 'className'>, DataFieldContextValue {
  children: ReactNode
  paddingY?: 'md'
}

const dataFieldColumnListVariants = tv({
  base: 'flex flex-col gap-y-4 *:flex-[1_0_0] @3xl:flex-row',
  variants: {
    paddingY: {
      md: 'py-4',
    },
    size: {
      lg: '*:px-5 @3xl:gap-x-5',
      md: '*:px-5 @3xl:gap-x-5',
      sm: '*:pr-2 @3xl:gap-x-3',
      xl: '*:px-5 @3xl:gap-x-5',
    },
  },
})

export function DataFieldColumnList({
  children,
  direction,
  paddingY,
  size,
}: DataFieldColumnListProps) {
  const context = use(DataFieldContext)
  return (
    <DataFieldContext
      value={{
        direction: direction ?? context.direction,
        size: size ?? context.size,
      }}
    >
      <div className="@container">
        <div
          className={dataFieldColumnListVariants({
            paddingY,
            size: size ?? context.size,
          })}
        >
          {children}
        </div>
      </div>
    </DataFieldContext>
  )
}

const dataFieldColumnTitleVariants = tv({
  base: 'font-semibold text-tremor-content-strong',
  variants: {
    size: {
      lg: 'text-lg',
      md: 'text-sm',
      sm: 'text-sm',
      xl: 'text-lg',
    },
  },
})

export function DataFieldColumnTitle({
  children,
  size,
  ...props
}: Omit<ComponentProps<'h4'>, 'className'> & DataFieldContextValue) {
  const context = use(DataFieldContext)
  return (
    <h4
      {...props}
      className={dataFieldColumnTitleVariants({
        size: size ?? context.size,
      })}
    >
      {children}
    </h4>
  )
}
